import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Table, Spinner, Button, Alert } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { simplePostCall } from '../../api/ApiServices';
import ApiConfig from '../../api/ApiConfig';
import "../../Styles/yellocalfcss/SelectStudent.css";

const AddDiscount = () => {
  const { id } = useParams(); // Get invoice ID from URL
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Data States
  const [invoiceData, setInvoiceData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  
  // Form State for Adding Discount
  const [newDiscount, setNewDiscount] = useState({
    amount: '',
    text: '',
    discount_type: 'new_discount_type', // Default as per your JSON
    particular: '' // specific item selection
  });

  useEffect(() => {
    loadInvoiceDetails();
    return () => { document.body.style.overflow = 'auto'; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- 1. Fetch Invoice Data ---
  const loadInvoiceDetails = async () => {
    setIsLoading(true);
    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id") || 1005),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      id: Number(id) || 2994 // Fallback to 2994 if no URL param
    };

    try {
      const response = await simplePostCall(
        ApiConfig.Getiscount, // Make sure this matches your API Endpoint key
        JSON.stringify(requestBody)
      );

      if (response?.result) {
        setInvoiceData(response.invoiceModel?.[0] || null);
        setStudentData(response.studentModel?.[0] || null);
        setLineItems(response.itemModel || []);
      } else {
        setMessage({ type: 'danger', text: response?.message || "No records found" });
      }
    } catch (error) {
      console.error("Error loading invoice:", error);
      setMessage({ type: 'danger', text: "Failed to load data." });
    } finally {
      setIsLoading(false);
    }
  };

  // --- 2. Add Discount Logic ---
  const handleAddDiscount = async () => {
    if (!newDiscount.amount || !newDiscount.text) {
      alert("Please enter amount and description");
      return;
    }

    setIsLoading(true);
    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id") || 1005),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      id: invoiceData?.fee_invoice_id || id, // Invoice ID
      amount: Number(newDiscount.amount),
      discount_type: newDiscount.discount_type,
      text: newDiscount.text
    };

    try {
      const response = await simplePostCall(
        ApiConfig.Adddiscount, // Assuming same endpoint uses POST logic for adding, or change to ApiConfig.AddDiscount
        JSON.stringify(requestBody)
      );

      if (response?.result) {
        setMessage({ type: 'success', text: "Discount added successfully!" });
        setNewDiscount({ amount: '', text: '', discount_type: 'new_discount_type', particular: '' }); // Reset Form
        loadInvoiceDetails(); // **Refresh Data**
      } else {
        setMessage({ type: 'danger', text: response?.message || "Failed to add discount" });
      }
    } catch (error) {
      console.error("Error adding discount:", error);
      setMessage({ type: 'danger', text: "Error submitting data." });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (isLoading && !invoiceData) {
    return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;
  }

  return (
    <div className="SelectStudent-container my-4">
      {/* Header */}
      <div className="SelectStudent-invoice-header d-flex justify-content-between align-items-center">
        <h4>Discount Management</h4>
        <button className="btn btn-success">Collect Fee</button>
      </div>

      {message.text && (
        <Alert variant={message.type} onClose={() => setMessage({ type: '', text: '' })} dismissible className="mt-3">
          {message.text}
        </Alert>
      )}

      <div className="SelectStudent-invoice-form mt-3 p-4">
        {/* Info Box Section - Dynamic Data */}
        <div style={{ border: '1px solid #dee2e6', padding: '15px', marginBottom: '20px', backgroundColor: '#fff' }}>
          <Row className="gy-2" style={{ fontSize: '13px' }}>
            <Col md={4}><strong>Admission No:</strong> {studentData?.student_admission_num || 'N/A'}</Col>
            <Col md={4}><strong>Academic Year:</strong> {invoiceData?.academic_year_name || 'N/A'}</Col>
            <Col md={4}><strong>Date:</strong> {formatDate(invoiceData?.fee_invoice_date)}</Col>
            <Col md={4}><strong>Name:</strong> {studentData?.student_name?.toUpperCase() || 'N/A'}</Col>
            <Col md={4}><strong>Division:</strong> {studentData?.class_division_name || 'N/A'}</Col>
            <Col md={4}><strong>Invoice No:</strong> {invoiceData?.fee_invoice_id || 'N/A'}</Col>
            <Col md={4}><strong>Grade:</strong> {studentData?.class_name || 'N/A'}</Col>
          </Row>
        </div>

        {/* Main Table */}
        <Table bordered responsive style={{ borderColor: '#dee2e6' }}>
          <thead style={{ fontSize: '14px', backgroundColor: '#f9f9f9' }}>
            <tr>
              <th style={{ width: '50px' }}>#</th>
              <th>Particulars</th>
              <th>Details / Type</th>
              <th style={{ width: '200px' }}>Amount</th>
            </tr>
          </thead>
          <tbody style={{ fontSize: '13px' }}>
            
            {/* Existing Items Loop */}
            {lineItems.map((item, index) => (
              <React.Fragment key={index}>
                <tr>
                  <td>{index + 1}</td>
                  <td>
                    <div className="fw-bold">{item.fee_category_name || "Fee Item"}</div>
                  </td>
                  <td>Existing Fee</td>
                  <td>$ {item.invoice_item_amount}</td>
                </tr>
                {/* Sub Items (e.g., Late Fee, existing discounts) */}
                {item.sub_item && item.sub_item.map((sub, subIndex) => (
                   <tr key={`sub-${subIndex}`}>
                   <td></td>
                   <td className="text-muted ps-4">L {sub.fee_category_name}</td>
                   <td>{sub.invoice_item_type}</td>
                   <td>$ {sub.invoice_item_amount}</td>
                 </tr>
                ))}
              </React.Fragment>
            ))}

            {/* --- ADD DISCOUNT INPUT ROW --- */}
            <tr style={{ backgroundColor: '#eefcf1' }}>
              <td className="fw-bold text-success">+</td>
              <td>
                <Form.Select 
                  size="sm"
                  value={newDiscount.particular}
                  onChange={(e) => setNewDiscount({...newDiscount, particular: e.target.value})}
                >
                  <option value="">Select Particulars (Optional)</option>
                  {lineItems.map(item => (
                     <option key={item.invoice_item_id} value={item.invoice_item_id}>
                       {item.fee_category_name}
                     </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <div className="d-flex gap-3 mb-2">
                  <Form.Check 
                    type="radio" 
                    label="Existing Discount" 
                    name="dType"
                    checked={newDiscount.discount_type === 'existing'}
                    onChange={() => setNewDiscount({...newDiscount, discount_type: 'existing'})}
                  />
                  <Form.Check 
                    type="radio" 
                    label="New Discount Type" 
                    name="dType" 
                    checked={newDiscount.discount_type === 'new_discount_type'}
                    onChange={() => setNewDiscount({...newDiscount, discount_type: 'new_discount_type'})}
                  />
                </div>
                <Form.Control 
                  size="sm" 
                  type="text" 
                  placeholder="Enter Description / Reason" 
                  value={newDiscount.text}
                  onChange={(e) => setNewDiscount({...newDiscount, text: e.target.value})}
                />
              </td>
              <td>
                <Form.Control 
                  size="sm" 
                  type="number" 
                  placeholder="Amount" 
                  value={newDiscount.amount}
                  onChange={(e) => setNewDiscount({...newDiscount, amount: e.target.value})}
                />
              </td>
            </tr>

            {/* Summary Footer */}
            <tr>
              <td colSpan="2" rowSpan="6"></td>
              <td className="fw-bold text-end">Sub Total</td>
              <td>$ {invoiceData?.fee_invoice_amount || 0}</td>
            </tr>
            <tr><td className="fw-bold text-end">Discount (-)</td><td>$ {invoiceData?.fee_invoice_amount_discount || 0}</td></tr>
            <tr><td className="fw-bold text-end">Bonus/Charges (+)</td><td>$ {invoiceData?.fee_invoice_amount_bonus || 0}</td></tr>
            <tr><td className="fw-bold text-end">Paid Amount</td><td>$ {invoiceData?.fee_invoice_total_amount_paid || 0}</td></tr>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <td className="fw-bold text-end">Total Amount To Pay</td>
              <td className="fw-bold fs-6">$ {invoiceData?.fee_invoice_amount_total || 0}</td>
            </tr>
          </tbody>
        </Table>

        <div className="d-flex justify-content-end mt-3">
          <Button 
            variant="primary" 
            className="px-4" 
            onClick={handleAddDiscount}
            disabled={isLoading}
          >
            {isLoading ? <Spinner as="span" animation="border" size="sm" /> : "Create Discount"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddDiscount;